/* eslint-disable react/prop-types */
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePage } from "../../../context/PageContext";
import { Container } from "../../Atoms/Container/Container.component";
import { HeroList } from "../../Templates/HeroList/HeroList.component";
import { ModuleList } from "../../Templates/ModuleList/ModuleList.component";
import { ErrorPage } from "../ErrorPage/ErrorPage.component";
import { ErrorBoundary } from "../../Organisms/ErrorBoundry/ErrorBoundary";
import { FrontendPage } from "../FrontendPage/FrontendPage.component";
import { SearchResults } from "@shared/components/Organisms/SearchResults/SearchResults.component";
import { useTranslations } from "@shared/context/useTranslations";
import { CategoryPageProps } from "@shared/types/CategoryTypes.interface";
import { ProductPageProps } from "@shared/types/ProductTypes.interface";
import { ReceiptPageProps } from "@shared/types/ReceiptType.interface";
import ProductList from "../ProductList/ProductList.component";
import ProductItem from "../Productitem/ProductItem.component";
import Basket from "../Basket/Basket.component";
import QuickOrder from "../QuickOrder/QuickOrder.component";
import S from "./DynamicPage.module.scss";
import { Favorites } from "../Favorites/Favorites.component";
import UserAccount from "../UserAccount/UserAccount.component";
import { Checkout } from "../Checkout/Checkout.component";
import { Receipt } from "../Receipt/Receipt.component";
import { OrderHistory } from "../OrderHistory/OrderHistory.component";
import AddressList from "../AddressList/AddressList.component";
// import { CraneSerialSearch } from "@shared/components/Templates/CraneSerialSearch/CraneSerialSearch.component";
import { useSearch } from "@shared/context/SearchContext";
import { useCrane } from "@shared/context/CraneContext";
import { PageLoader } from "@shared/components/Atoms/PageLoader/PageLoader.component";
import { useBasket } from "@shared/context/BasketContext";
import { Contacts } from "../Contacts/Contacts.component";
// import { getRoutePathByPageType } from "@shared/helpers/findPathForSystemSite";
// import { CranePage } from "../CranePage/CranePage.component";

export const DynamicPage = () => {
    const location = useLocation();
    const lastCheckedPageRef = useRef<string>("");
    const { getTranslation } = useTranslations();

    const { page, isLoading, error, fetchPage, setPage, statusCode } = usePage();
    const { isLoading: isSearchLoading } = useSearch();
    const { isLoading: isCraneLoading } = useCrane();
    const { isLoading: isBasketLoading } = useBasket();

    const isPageLoading = useMemo(
        () => isLoading || isSearchLoading || isCraneLoading || isBasketLoading,
        [isLoading, isSearchLoading, isCraneLoading, isBasketLoading]
    );

    // const cranePath = getRoutePathByPageType("Crane") || "";

    // const getDynamicPagePath = (pathname: string) => {
    //     // If the path is for crane, return crane path only
    //     if (pathname.startsWith(cranePath)) {
    //         const cranePathSegments = pathname.startsWith("/") ? pathname.split("/").slice(1) : pathname.split("/");
    //         console.log('CRANE PAGE DETECTED', pathname);
    //         console.log('SEG', cranePathSegments);
    //         console.log('DYN', cranePathSegments.slice(2).join("/"));

    //         return `/${cranePathSegments.slice(2).join("/")}`;
    //     }

    //     return pathname
    // };

    // Ensure fetchPage is only called when pathname changes
    const checkPage = useCallback(
        async (pathname: string) => {
            if (lastCheckedPageRef.current !== pathname) {
                // //TODO: check for crane path and remove parameter part of url before fetching
                // if (pathname.startsWith(cranePath)) {
                //     // const cranePathSegments = pathname.startsWith("/") ? pathname.split("/").slice(1) : pathname.split("/");
                //     console.log('CRANE PAGE DETECTED', getDynamicPagePath(pathname));
                // }

                lastCheckedPageRef.current = pathname;
                await fetchPage(pathname);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fetchPage, setPage]
    );

    // Watch for changes in location.pathname
    useEffect(() => {
        // const dynamicPath = getDynamicPagePath(location.pathname) || location.pathname;
        // console.log('current path', location.pathname, dynamicPath);
        checkPage(location.pathname);
    }, [checkPage, location.pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const renderBody = () => {
        if (error || statusCode === 404) {
            return (
                <Container>
                    <ErrorPage />
                </Container>
            );
        }
        if (!page) {
            return (
                <Container>
                    <div>{getTranslation("general.no-content-found")}</div>
                </Container>
            );
        }

        const { name, props } = page;

        return (
            <FrontendPage {...page}>
                <Fragment>
                    <div className={S.title}>
                        <h2>{name}</h2>
                    </div>
                    <ErrorBoundary>
                        {props?.heroList?.items && <HeroList heroLists={props.heroList.items} />}
                    </ErrorBoundary>

                    <ErrorBoundary>
                        {props?.modules && <ModuleList modules={props.modules.items} />}
                    </ErrorBoundary>
                </Fragment>
            </FrontendPage>
        );
    };

    const getPageComponents = (): Record<string, ReactNode> => {
        return {
            ContentPage: renderBody(),

            ProductPage: (
                <FrontendPage {...page}>
                    <ProductItem {...(page as ProductPageProps)} />
                </FrontendPage>
            ),
            CategoryPage: (
                <FrontendPage {...page}>
                    <ProductList {...(page as CategoryPageProps)} />
                </FrontendPage>
            ),
            Search: (
                <FrontendPage {...page}>
                    <SearchResults />
                </FrontendPage>
            ),

            // TODO: build these pages
            UserAccount: (
                <FrontendPage {...page} hideSidebar>
                    <UserAccount />
                </FrontendPage>
            ),

            UserPricelist: (
                <FrontendPage {...page} hideSidebar>
                    <h1>Price list</h1>
                </FrontendPage>
            ),

            Address: (
                <FrontendPage {...page} hideSidebar>
                    <AddressList />
                </FrontendPage>
            ),
            Favorites: (
                <FrontendPage {...page} hideSidebar>
                    <Favorites />
                </FrontendPage>
            ),
            Contacts: (
                <FrontendPage {...page} hideSidebar>
                    <Contacts />
                </FrontendPage>
            ),
            Checkout: (
                <Container>
                    <ErrorBoundary>
                        <Checkout />
                    </ErrorBoundary>
                </Container>
            ),
            Orderhistory: (
                <FrontendPage {...page} hideSidebar>
                    <OrderHistory />
                </FrontendPage>
            ),
            Quickorder: (
                <Container>
                    <ErrorBoundary>
                        <QuickOrder />
                    </ErrorBoundary>
                </Container>
            ),
            Receipt: (
                <Container>
                    <ErrorBoundary>
                        <Receipt {...(page as unknown as ReceiptPageProps)} />
                    </ErrorBoundary>
                </Container>
            ),
            Basket: (
                <Container>
                    <ErrorBoundary>
                        <Basket />
                    </ErrorBoundary>
                </Container>
            ),

            "404": <ErrorPage />,
        };
    };

    // TODO: check all titles on frontendPages (basket, quick order)
    const getPageType = () => {
        // Special pages
        if (page?.type === "frontendPage") {
            const pageType = page?.props?.pageType || (statusCode === 404 ? "404" : "ErrorPage");
            return pageType.replace(" ", "");
        }

        // PLP, PDP, Content pages
        return page?.pageType || (statusCode === 404 ? "404" : "ErrorPage");
    };

    const pageComponents = getPageComponents();
    const pageType = getPageType();

    return (
        <Fragment>
            <PageLoader isPageLoading={isPageLoading} />
            {pageComponents[pageType]}
        </Fragment>
    );
};

export default DynamicPage;
